import { observer } from 'mobx-react-lite';
import { DefaultButton } from '@fluentui/react/lib/Button';
import { Callout, DirectionalHint } from '@fluentui/react/lib/Callout';
import { logUsage } from 'owa-analytics';
import setAttachmentShouldShowContextMenu from 'owa-attachment-full-data/lib/actions/setAttachmentShouldShowContextMenu';
import { ConversationAttachmentWellViewState } from 'owa-attachment-well-data';
import setConversationAttachmentWellVisible from 'owa-attachment-well-data/lib/mutators/setConversationAttachmentWellVisible';
import loc, { format } from 'owa-localize';
import { NAttachments } from 'owa-locstrings/lib/strings/nattachments.locstring.json';
import { InstrumentationContext } from 'owa-mail-store';
import * as React from 'react';
import ConversationAttachmentWell from './ConversationAttachmentWell';
import { OneAttachment } from './ConversationAttachmentWellButton.locstring.json';

const styles = require('./ConversationAttachmentWellButton.scss');

export interface ConversationAttachmentWellButtonProps {
    viewState: ConversationAttachmentWellViewState;
    instrumentationContext: InstrumentationContext;
    canLoadMore: boolean;
    className?: string;
}

export default observer(function ConversationAttachmentWellButton(
    props: ConversationAttachmentWellButtonProps
) {
    React.useEffect(
        () => {
            if (!dataPointLogged.current && props.viewState.docViewAttachments.length > 0) {
                dataPointLogged.current = true;
                logUsage('ConversationAttachmentWellButtonShown');
            }
        }
        /* No dependency array, as we want to run this every update */
    );
    const buttonElement = React.useRef<HTMLElement | null>();
    const dataPointLogged = React.useRef(false);
    const dismissCallout = () => {
        setConversationAttachmentWellVisible(props.viewState, false);
        props.viewState.docViewAttachments.forEach(attachmentViewState => {
            setAttachmentShouldShowContextMenu(attachmentViewState, false);
        });
    };
    const onShowMenuClicked = () => {
        if (props.viewState.isVisible) {
            dismissCallout();
        } else {
            setConversationAttachmentWellVisible(props.viewState, true);
            logUsage('ConversationAttachmentWellButtonClicked', null, {
                cosmosOnlyData: JSON.stringify({
                    numberOfAttachments: props.viewState.docViewAttachments.length,
                }),
            });
        }
    };
    const getAriaLabel = (): string => {
        const totalCount: number = props.viewState.docViewAttachments.length;
        if (totalCount > 1) {
            return format(loc(NAttachments), totalCount);
        } else {
            return format(loc(OneAttachment), totalCount);
        }
    };
    const numberOfAttachments = props.viewState.docViewAttachments.length;
    if (numberOfAttachments === 0) {
        return null;
    }
    const buttonText = numberOfAttachments + (props.canLoadMore ? '+' : '');
    return (
        <div className={props.className || ''}>
            <div ref={r => (buttonElement.current = r)}>
                <DefaultButton
                    onClick={onShowMenuClicked}
                    text={buttonText}
                    className={styles.button}
                    iconProps={{
                        iconName: 'Attach',
                        styles: {
                            root: styles.icon,
                        },
                    }}
                    menuIconProps={{
                        iconName: 'ChevronDown',
                        styles: {
                            root: styles.menuIcon,
                        },
                    }}
                    ariaLabel={getAriaLabel()}
                    styles={{
                        label: styles.label,
                    }}
                />
            </div>
            {props.viewState.isVisible ? (
                <Callout
                    setInitialFocus={true}
                    isBeakVisible={false}
                    target={buttonElement.current}
                    onDismiss={dismissCallout}
                    directionalHint={DirectionalHint.bottomRightEdge}>
                    <ConversationAttachmentWell
                        viewState={props.viewState}
                        instrumentationContext={props.instrumentationContext}
                        onBeforePreview={dismissCallout}
                    />
                </Callout>
            ) : null}
        </div>
    );
});
